import { useState, useEffect } from 'react';
import { ArrowRightLeft, Plus, Edit, Trash2, Power, Loader2, X, Clock } from 'lucide-react';
import { approvalAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Generate time options every 30 minutes
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m of [0, 30]) {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    TIME_OPTIONS.push({ value: `${hh}:${mm}`, label: `${h12}:${mm} ${ampm}` });
  }
}

function splitDateTime(isoStr) {
  if (!isoStr) return { date: '', time: '09:00' };
  const [date, time] = isoStr.slice(0, 16).split('T');
  // Round time to nearest 30 min option
  const [hh, mm] = (time || '09:00').split(':');
  const rounded = parseInt(mm) < 15 ? '00' : parseInt(mm) < 45 ? '30' : '00';
  const roundedH = parseInt(mm) >= 45 ? String(parseInt(hh) + 1).padStart(2, '0') : hh;
  return { date, time: `${roundedH.padStart(2, '0')}:${rounded}` };
}

function combineDateTime(date, time) {
  if (!date || !time) return '';
  return `${date}T${time}`;
}

export default function DelegationsPage() {
  const { user } = useAuth();
  const [delegations, setDelegations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const isAdmin = ['admin', 'it_manager'].includes((user?.role_name || '').toLowerCase().replace(/\s+/g, '_'));

  const emptyForm = { delegator: '', delegate: '', start_date: '', start_time: '09:00', end_date: '', end_time: '18:00', reason: '' };
  const [form, setForm] = useState(emptyForm);

  const fetch = async () => {
    setLoading(true);
    try {
      const [dRes, uRes] = await Promise.allSettled([
        approvalAPI.listDelegations(),
        userAPI.list({ page_size: 500, role: 'approver,it_manager,admin' }),
      ]);
      if (dRes.status === 'fulfilled') setDelegations(dRes.value.data?.results || dRes.value.data || []);
      if (uRes.status === 'fulfilled') setUsers(uRes.value.data?.results || uRes.value.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    const start = splitDateTime(d.start_date_display || d.start_date);
    const end = splitDateTime(d.end_date_display || d.end_date);
    setForm({
      delegator: d.delegator || '',
      delegate: d.delegate,
      start_date: start.date, start_time: start.time,
      end_date: end.date,   end_time: end.time,
      reason: d.reason || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.delegate || !form.start_date || !form.start_time || !form.end_date || !form.end_time) {
      toast.error('Delegate, Start and End date+time are required'); return;
    }
    const data = { ...form };
    if (!data.delegator) data.delegator = user?.id;
    data.start_date = combineDateTime(form.start_date, form.start_time);
    data.end_date = combineDateTime(form.end_date, form.end_time);
    try {
      if (editingId) {
        await approvalAPI.updateDelegation(editingId, data);
        toast.success('Delegation updated');
      } else {
        await approvalAPI.createDelegation(data);
        toast.success('Delegation created');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this delegation?')) return;
    try { await approvalAPI.deleteDelegation(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (d) => {
    try {
      if (d.is_active) { await approvalAPI.deactivateDelegation(d.id); toast.success('Delegation stopped'); }
      else { await approvalAPI.activateDelegation(d.id); toast.success('Delegation reactivated'); }
      fetch();
    } catch { toast.error('Failed'); }
  };

  // Status label
  const statusBadge = (d) => {
    if (!d.is_active) return <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">Expired / Stopped</span>;
    const today = new Date().toISOString().slice(0, 10);
    if (d.start_date > today) return <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">Upcoming</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 font-medium">Active</span>;
  };

  return (
    <div data-testid="delegations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <ArrowRightLeft className="w-6 h-6 text-sky-500" /> Approval Delegations
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Delegate your approval authority when you're on leave. Delegations auto-expire after the end date.
          </p>
        </div>
        <button onClick={openCreate}
          className="bg-sky-500 text-white px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600 font-medium"
          data-testid="new-delegation-btn">
          <Plus className="w-4 h-4" /> New Delegation
        </button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6" data-testid="delegation-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 dark:text-white">
              {editingId ? 'Edit Delegation' : 'Create New Delegation'}
            </h3>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-slate-400 hover:text-slate-600 p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Delegator — Admin sees selector, Approver sees read-only */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1.5">
                Delegator (on leave)
              </label>
              {isAdmin ? (
                <select value={form.delegator} onChange={e => setForm({...form, delegator: e.target.value})}
                  className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                  <option value="">Myself</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
              ) : (
                <div className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
                  {user?.first_name} {user?.last_name} <span className="text-slate-400">(You)</span>
                </div>
              )}
            </div>
            {/* Delegate */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1.5">Delegate (takes over) *</label>
              <select value={form.delegate} onChange={e => setForm({...form, delegate: e.target.value})}
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                <option value="">Select delegate...</option>
                {users.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
              </select>
            </div>
            {/* Start Date + Time */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1.5">From *</label>
              <div className="flex gap-2">
                <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}
                  className="flex-1 border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
                <select value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})}
                  className="w-28 border dark:border-slate-700 rounded-xl px-2 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                  {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            {/* End Date + Time */}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1.5">To *</label>
              <div className="flex gap-2">
                <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}
                  className="flex-1 border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
                <select value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})}
                  className="w-28 border dark:border-slate-700 rounded-xl px-2 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                  {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1.5">Reason (leave type)</label>
              <input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}
                placeholder="e.g. Annual leave, Medical leave"
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="bg-sky-500 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-sky-600">
              {editingId ? 'Save Changes' : 'Create Delegation'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }}
              className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-5 py-2.5 rounded-xl text-sm hover:bg-slate-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delegations List */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : delegations.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <ArrowRightLeft className="w-12 h-12 mx-auto text-slate-200 dark:text-slate-600 mb-3" />
          <p className="text-slate-500 font-medium">No delegations yet</p>
          <p className="text-slate-400 text-sm mt-1">Create a delegation to assign your approval authority when you're away</p>
          <button onClick={openCreate} className="mt-4 bg-sky-500 text-white px-4 py-2 rounded-xl text-sm hover:bg-sky-600">
            + New Delegation
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {delegations.map(d => (
            <div key={d.id} className={`bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-5 transition-all ${!d.is_active ? 'opacity-60' : ''}`}
              data-testid={`delegation-${d.id}`}>
              <div className="flex items-start justify-between gap-4">
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="font-semibold text-slate-800 dark:text-white">{d.delegator_name}</span>
                    <ArrowRightLeft className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="font-semibold text-sky-600 dark:text-sky-400">{d.delegate_name}</span>
                    {statusBadge(d)}
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {d.start_date_display
                        ? new Date(d.start_date_display + ':00').toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
                        : '—'}
                      {' → '}
                      {d.end_date_display
                        ? new Date(d.end_date_display + ':00').toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
                        : '—'}
                    </span>
                    {d.reason && <span>💬 {d.reason}</span>}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => openEdit(d)} title="Edit"
                    className="p-2 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(d.id)} title="Delete"
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleToggle(d)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                      d.is_active
                        ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-900/20 dark:text-rose-400'
                        : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400'
                    }`}>
                    <Power className="w-3.5 h-3.5" />
                    {d.is_active ? 'Stop' : 'Reactivate'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info box */}
      <div className="mt-5 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-xl p-4 text-xs text-sky-800 dark:text-sky-300">
        <strong>How it works:</strong> During an active delegation, when an approval is required for the delegator, the delegate can also approve/reject it. Delegations automatically expire after the end date. Each delegation is limited to one reactivation after manual stop.
      </div>
    </div>
  );
}
